import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, createVNode as _createVNode, normalizeStyle as _normalizeStyle, createElementBlock as _createElementBlock, withCtx as _withCtx, Fragment as _Fragment } from "vue";
const _hoisted_1 = {
    class: "flex flex-col flex-1 [&_.min-h-0]:min-h-max [&_.overflow-x-hidden]:overflow-visible [&_.overflow-x-hidden]:flex [&_.overflow-x-hidden]:flex-col",
    "data-testid": "project-allocations"
};
const _hoisted_2 = {
    class: "flex items-center gap-4 px-5 py-5 border-b border-(--colors-border-base-soft) dark:border-(--colors-border-base) text-base leading-6 text-(--colors-text-body) dark:text-(--colors-text-white-secondary)",
    "data-testid": "project-allocations-totals"
};
const _hoisted_3 = {
    class: "font-bold text-(--colors-text-heading) dark:text-(--colors-text-white-default)",
    "data-testid": "project-allocations-total-count"
};
const _hoisted_4 = {
    class: "font-bold text-(--colors-text-heading) dark:text-(--colors-text-white-default)",
    "data-testid": "project-allocations-total-amount"
};
const _hoisted_5 = { class: "text-sm font-medium text-(--colors-text-heading)" };
const _hoisted_6 = { class: "text-xs text-(--colors-text-body-subtle) mt-1" };
const _hoisted_7 = ["id"];
const _hoisted_8 = {
    key: 0,
    class: "flex flex-col gap-3 text-sm",
    "data-testid": "project-allocations-detail"
};
const _hoisted_9 = { class: "flex justify-between gap-4" };
const _hoisted_10 = { class: "text-(--colors-text-body-subtle)" };
const _hoisted_11 = { class: "font-medium text-(--colors-text-heading) text-right" };
const _hoisted_12 = { class: "flex justify-between gap-4" };
const _hoisted_13 = { class: "text-(--colors-text-body-subtle)" };
const _hoisted_14 = { class: "font-medium text-(--colors-text-heading) text-right" };
const _hoisted_15 = { class: "flex justify-between gap-4" };
const _hoisted_16 = { class: "text-(--colors-text-body-subtle)" };
const _hoisted_17 = { class: "font-medium text-(--colors-text-heading) text-right" };
const _hoisted_18 = { class: "flex justify-between gap-4" };
const _hoisted_19 = { class: "text-(--colors-text-body-subtle)" };
const _hoisted_20 = { class: "font-medium text-(--colors-text-heading) text-right" };
import { computed, h, ref, useId, watch } from "vue";
import { RouterLink, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { Avatar, Button, DataTable, Modal, Pager, TableShell, WidgetShell, useTableQuery, useTableViews, } from "@eudonet/opal";
import { useProfileStore } from "@/modules/settings/public";
import { useProjectAmounts } from "../../composables/useProjectAmounts";
import { formatDate } from "@/shared/utils/date-formatting";
import { buildTableLabels } from "@/shared/composables/tableLabels";
import { useListSettings } from "@/shared/composables/useListSettings";
import { useListRowSelection } from "@/shared/composables/useListRowSelection";
import { useAppToast } from "@/shared/composables/useAppToast";
import ListFloatActionBar from "@/shared/components/ListFloatActionBar.vue";
import { logger } from "@/infrastructure/services/LoggerService";
import { getProjectDonations } from "../../../infrastructure/api/projectsApi";
import ResumeLoadError from "./ResumeLoadError.vue";
const EXPORT_PAGE_SIZE = 50;
export default /*@__PURE__*/ _defineComponent({
    __name: 'ProjectTabAllocations',
    props: {
        projectId: {},
        projectName: {},
        donations: {},
        query: {},
        loading: { type: Boolean },
        error: { type: Boolean }
    },
    emits: ["retry", "query-change"],
    setup(__props, { emit: __emit }) {
        const ROW_OPTIONS = [5, 10, 25];
        /** Largest page the backend serves — the export walks the filtered list in these steps. */
        const props = __props;
        const emit = __emit;
        const { t, locale } = useI18n();
        const router = useRouter();
        const profileStore = useProfileStore();
        const { formatProjectAmount, formatProjectAmountForExport } = useProjectAmounts();
        const { toastSuccess, toastError } = useAppToast();
        // ── Table query — seeded from the query the parent last read with ─────────────
        // The tab unmounts on every tab switch, so its state must come back from the parent's, or the
        // rows on screen (page 3, filtered) would sit under a table that believes it shows page 1.
        const COLUMN_TO_SORT = {
            donor: "Donor",
            amount: "Amount",
            date: "Date",
        };
        const SORT_TO_COLUMN = {
            Donor: "donor",
            Amount: "amount",
            Date: "date",
        };
        function filtersFromQuery(query) {
            const filters = {};
            if (query.donor)
                filters.donor = { type: "equals", value: query.donor };
            return filters;
        }
        const tableQuery = useTableQuery({
            page: props.query.page,
            pageSize: props.query.pageSize,
            search: props.query.donor ?? "",
            filters: filtersFromQuery(props.query),
            sort: props.query.sortBy
                ? { column: SORT_TO_COLUMN[props.query.sortBy], direction: props.query.sortOrder ?? "desc" }
                : null,
            activeViewId: "all",
            filterLabels: () => ({ donor: t("projects.detail.allocations.filter.donor") }),
        });
        // Level 1: a single preset tab. No saved lists exist for this tab, hence no dropdown either.
        const tableViews = useTableViews({
            initialViews: [
                {
                    id: "all",
                    label: t("projects.detail.allocations.allDonations"),
                    isPreset: true,
                    isDefault: true,
                    querySnapshot: {},
                },
            ],
        });
        const tableLabels = computed(() => buildTableLabels(t));
        /** The read the current table state asks for. */
        const apiQuery = computed(() => {
            const donorFilter = tableQuery.state.filters["donor"];
            const sort = tableQuery.state.sort;
            return {
                page: tableQuery.state.page,
                pageSize: tableQuery.state.pageSize,
                donor: donorFilter?.type === "equals" ? String(donorFilter.value).trim() || undefined : undefined,
                sortBy: sort ? COLUMN_TO_SORT[sort.column] : undefined,
                sortOrder: sort ? sort.direction : undefined,
            };
        });
        // The pill's ✕ mutates `filters` directly on the shared query, so the emit is driven by the
        // derived query rather than by the handlers that set it.
        watch(apiQuery, (next, previous) => {
            if (JSON.stringify(next) !== JSON.stringify(previous))
                emit("query-change", next);
        });
        // The Level-1 search box IS the "Donateur contient" filter: it writes the same pill the Filtrer
        // panel does, so there is one donor fragment on the wire whichever way it was typed.
        function donorFilterValue() {
            const filter = tableQuery.state.filters["donor"];
            return filter?.type === "equals" ? String(filter.value) : "";
        }
        watch(() => tableQuery.state.search ?? "", (next) => {
            const term = next.trim();
            if (term === donorFilterValue())
                return;
            if (term)
                tableQuery.setFilter("donor", { type: "equals", value: term });
            else
                tableQuery.removeFilter("donor");
        });
        // Removing the Donateur pill (its ✕, Tout effacer, Réinitialiser) empties the box too, or the
        // box would keep showing a term the table no longer filters on.
        watch(() => tableQuery.state.filters["donor"], (filter) => {
            if (!filter && (tableQuery.state.search ?? "") !== "")
                tableQuery.setSearch("");
        });
        // ── Rows ──────────────────────────────────────────────────────────────────────
        function toRow(donation) {
            return {
                id: donation.donationId,
                label: donation.donationLabel || "—",
                donor: donation.donorDisplayName ?? "—",
                date: donation.donationDate
                    ? formatDate(donation.donationDate, locale.value, profileStore.profile?.dateFormat)
                    : "—",
                amount: formatProjectAmount(donation.allocatedAmount),
                rawAmount: donation.allocatedAmount,
            };
        }
        const rows = computed(() => props.donations.items.map(toRow));
        // Header row (h-10 + its border) plus one h-12 row per page slot: the table keeps the height of
        // a full page even when the page is short or empty.
        const rowsAreaStyle = computed(() => ({
            minHeight: `calc(2.5rem + 1px + ${tableQuery.state.pageSize} * 3rem)`,
        }));
        const totalPages = computed(() => Math.max(1, Math.ceil(props.donations.matchingDonations / Math.max(1, tableQuery.state.pageSize))));
        // ── Selection + export ────────────────────────────────────────────────────────
        const { selectColumn, selectedIds, effectiveCount, allPagesSelected, totalAcrossAllPages, clearSelection, onSelectAllPages, } = useListRowSelection({
            t,
            rows: () => rows.value,
            getRowId: (row) => row.id,
            totalCount: () => props.donations.matchingDonations,
        });
        const exporting = ref(false);
        const bulkActions = computed(() => [
            {
                id: "export",
                label: t("projects.detail.allocations.export"),
                icon: "ri-upload-2-line",
                variant: "text",
            },
        ]);
        function onBulkAction(actionId, context) {
            if (actionId === "export")
                void exportSelection(context.allPagesSelected);
        }
        /** Every row matching the current filters, in the current order, across every page. */
        async function fetchAllMatchingRows() {
            const all = [];
            let page = 1;
            for (;;) {
                const summary = await getProjectDonations(props.projectId, {
                    ...apiQuery.value,
                    page,
                    pageSize: EXPORT_PAGE_SIZE,
                });
                all.push(...summary.items.map(toRow));
                if (summary.items.length === 0 || all.length >= summary.matchingDonations)
                    break;
                page += 1;
            }
            return all;
        }
        // Excel only opens the accented donor names as UTF-8 when the file starts with the byte-order mark.
        const UTF8_BOM = String.fromCharCode(0xfeff);
        // A cell starting with = + - @ (or a tab / CR) is executed as a formula by Excel and LibreOffice
        // when the file is opened, and donor names and donation labels are user-authored: the leading
        // apostrophe forces text.
        const CSV_FORMULA_PREFIX = /^[=+\-@\t\r]/;
        function csvCell(value) {
            const safe = CSV_FORMULA_PREFIX.test(value) ? `'${value}` : value;
            return `"${safe.replace(/"/g, '""')}"`;
        }
        function downloadCsv(rowsToExport) {
            const header = [
                t("projects.detail.allocations.columns.donation"),
                t("projects.detail.allocations.columns.donor"),
                t("projects.detail.allocations.columns.amount"),
                t("projects.detail.allocations.columns.date"),
            ];
            const lines = [
                header.map(csvCell).join(";"),
                ...rowsToExport.map((row) => [row.label, row.donor, formatProjectAmountForExport(row.rawAmount), row.date]
                    .map(csvCell)
                    .join(";")),
            ];
            const blob = new Blob([UTF8_BOM + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const anchor = document.createElement("a");
            anchor.href = url;
            anchor.download = `allocations-${new Date().toISOString().slice(0, 10)}.csv`;
            anchor.click();
            URL.revokeObjectURL(url);
        }
        async function exportSelection(allPages) {
            exporting.value = true;
            try {
                const ticked = new Set(selectedIds.value);
                const rowsToExport = allPages
                    ? await fetchAllMatchingRows()
                    : rows.value.filter((row) => ticked.has(row.id));
                downloadCsv(rowsToExport);
                toastSuccess("projects.detail.allocations.exportSuccess", { count: rowsToExport.length });
                clearSelection();
            }
            catch (e) {
                logger.error("Project allocations export failed", e);
                toastError("projects.detail.allocations.exportError");
            }
            finally {
                exporting.value = false;
            }
        }
        // ── Donation details modal ────────────────────────────────────────────────────
        const detailTitleId = useId();
        const detailOpen = ref(false);
        const detailRow = ref(null);
        function openDetail(row) {
            detailRow.value = row;
            detailOpen.value = true;
        }
        function openDonationRecord() {
            if (!detailRow.value)
                return;
            detailOpen.value = false;
            void router.push({ name: "DonationDetail", params: { donationId: detailRow.value.id } });
        }
        // ── Columns ───────────────────────────────────────────────────────────────────
        // Server-side sort: the header menu writes the query's sort, the watcher above re-reads.
        const sortItems = (columnId) => [
            { label: t("table.sortAZ"), onClick: () => tableQuery.setSort(columnId, "asc") },
            { label: t("table.sortZA"), onClick: () => tableQuery.setSort(columnId, "desc") },
        ];
        const columnSettings = useListSettings({
            entity: "project-allocations",
            columns: () => [
                { key: "don", label: t("projects.detail.allocations.columns.donation"), visible: true, pinned: true, disabled: true },
                { key: "donor", label: t("projects.detail.allocations.columns.donor"), visible: true, pinned: true, disabled: true },
                { key: "amount", label: t("projects.detail.allocations.columns.amount"), visible: true, pinned: false },
                { key: "date", label: t("projects.detail.allocations.columns.date"), visible: true, pinned: false },
            ],
            defaultOrder: ["don", "donor", "amount", "date"],
            defaultPinning: { left: ["select", "don", "donor"], right: [] },
        });
        const columnOrder = computed(() => ["select", ...columnSettings.order.value]);
        const columnPinning = computed(() => ({
            left: ["select", ...(columnSettings.pinning.value.left ?? []).filter((key) => key !== "select")],
            right: columnSettings.pinning.value.right ?? [],
        }));
        const columns = computed(() => [
            selectColumn,
            {
                key: "label",
                id: "don",
                header: t("projects.detail.allocations.columns.donation"),
                locked: true,
                draggable: false,
                cell: ({ row }) => h(RouterLink, {
                    to: { name: "DonationDetail", params: { donationId: row.original.id } },
                    class: "text-sm font-medium text-(--colors-text-link) hover:underline truncate",
                    "data-testid": `project-allocation-donation-${row.original.id}`,
                }, () => row.original.label),
            },
            {
                key: "donor",
                header: t("projects.detail.allocations.columns.donor"),
                locked: true,
                draggable: false,
                cell: ({ row }) => h("div", { class: "flex items-center gap-2 min-w-0" }, [
                    h(Avatar, { name: row.original.donor, size: "xs", avatarStyle: "square", presentational: true }),
                    h("button", {
                        type: "button",
                        class: "text-sm font-medium text-(--colors-text-link) hover:underline text-left truncate",
                        "data-testid": `project-allocation-donor-${row.original.id}`,
                        onClick: () => openDetail(row.original),
                    }, row.original.donor),
                ]),
                headerMenuItems: sortItems("donor"),
            },
            {
                key: "amount",
                header: t("projects.detail.allocations.columns.amount"),
                draggable: true,
                cell: ({ row }) => h("span", { class: "font-semibold text-(--colors-text-heading)" }, row.original.amount),
                headerMenuItems: sortItems("amount"),
            },
            {
                key: "date",
                header: t("projects.detail.allocations.columns.date"),
                draggable: true,
                cell: ({ row }) => h("span", { class: "text-(--colors-text-body-subtle)" }, row.original.date),
                headerMenuItems: sortItems("date"),
            },
        ]);
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createVNode(_unref(WidgetShell), {
                    header: _unref(t)('projects.detail.allocations.title'),
                    loading: __props.loading,
                    stretch: ""
                }, {
                    default: _withCtx(() => [
                        (__props.error)
                            ? (_openBlock(), _createBlock(ResumeLoadError, {
                                key: 0,
                                testid: "project-allocations-error",
                                onRetry: _cache[0] || (_cache[0] = ($event) => (emit('retry')))
                            }))
                            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                _createVNode(_unref(TableShell), {
                                    query: _unref(tableQuery),
                                    views: _unref(tableViews),
                                    labels: tableLabels.value,
                                    "column-settings": _unref(columnSettings),
                                    "show-view-selector": true,
                                    "show-controls": false,
                                    "show-search": "",
                                    "search-placeholder": _unref(t)('projects.detail.allocations.searchPlaceholder'),
                                    loading: __props.loading && rows.value.length === 0,
                                    bordered: ""
                                }, {
                                    table: _withCtx(() => [
                                        _createElementVNode("div", _hoisted_2, [
                                            _createElementVNode("p", null, [
                                                _createTextVNode(_toDisplayString(_unref(t)("projects.detail.allocations.totals.donations")) + " ", 1 /* TEXT */),
                                                _createElementVNode("strong", _hoisted_3, _toDisplayString(__props.donations.totalDonations), 1 /* TEXT */)
                                            ]),
                                            _createElementVNode("p", null, [
                                                _createTextVNode(_toDisplayString(_unref(t)("projects.detail.allocations.totals.amount")) + " ", 1 /* TEXT */),
                                                _createElementVNode("strong", _hoisted_4, _toDisplayString(_unref(formatProjectAmount)(__props.donations.totalAllocated)), 1 /* TEXT */)
                                            ])
                                        ]),
                                        (!__props.loading && rows.value.length === 0 && _unref(tableQuery).hasActiveFilters.value)
                                            ? (_openBlock(), _createElementBlock("div", {
                                                key: 0,
                                                class: "flex flex-col flex-1 items-center py-16 gap-3 text-center",
                                                style: _normalizeStyle(rowsAreaStyle.value),
                                                "data-testid": "project-allocations-no-match"
                                            }, [
                                                _cache[4] || (_cache[4] = _createElementVNode("div", { class: "w-12 h-12 rounded-full bg-(--colors-background-white-tertiary) flex items-center justify-center" }, [
                                                    _createElementVNode("span", {
                                                        class: "ri-hand-coin-line text-2xl text-(--colors-text-disabled)",
                                                        "aria-hidden": "true"
                                                    })
                                                ], -1 /* CACHED */)),
                                                _createElementVNode("div", null, [
                                                    _createElementVNode("p", _hoisted_5, _toDisplayString(_unref(t)("projects.detail.allocations.noMatchTitle")), 1 /* TEXT */),
                                                    _createElementVNode("p", _hoisted_6, _toDisplayString(_unref(t)("projects.detail.allocations.noMatchHint")), 1 /* TEXT */)
                                                ]),
                                                _createVNode(_unref(Button), {
                                                    variant: "secondary",
                                                    size: "sm",
                                                    label: _unref(t)('projects.detail.allocations.resetFilters'),
                                                    "data-testid": "project-allocations-reset-filters",
                                                    onClick: _cache[1] || (_cache[1] = ($event) => (_unref(tableQuery).clearAllFilters()))
                                                }, null, 8 /* PROPS */, ["label"])
                                            ], 4 /* STYLE */))
                                            : (_openBlock(), _createBlock(_unref(DataTable), {
                                                key: 1,
                                                columns: columns.value,
                                                data: rows.value,
                                                "column-visibility": _unref(columnSettings).visibility,
                                                "column-order": columnOrder.value,
                                                "column-pinning": columnPinning.value,
                                                "column-sizing": _unref(columnSettings).widths.value,
                                                "has-settings": "",
                                                "settings-aria-label": _unref(t)('columnSettings.title'),
                                                "sticky-header": "",
                                                class: "flex-1 h-auto!",
                                                style: _normalizeStyle(rowsAreaStyle.value),
                                                "empty-text": _unref(t)('projects.detail.resume.donations.empty'),
                                                "data-testid": "project-allocations-table",
                                                onSettingsClick: _unref(columnSettings).openDrawer,
                                                "onUpdate:columnSizing": _unref(columnSettings).handleSizingChange
                                            }, null, 8 /* PROPS */, ["columns", "data", "column-visibility", "column-order", "column-pinning", "column-sizing", "settings-aria-label", "style", "empty-text", "onSettingsClick", "onUpdate:columnSizing"]))
                                    ]),
                                    pagination: _withCtx(() => [
                                        _createVNode(_unref(Pager), {
                                            "current-page": _unref(tableQuery).state.page,
                                            "total-pages": totalPages.value,
                                            "total-items": __props.donations.matchingDonations,
                                            "items-per-page": _unref(tableQuery).state.pageSize,
                                            "rows-per-page-options": ROW_OPTIONS,
                                            "rows-per-page-label": _unref(t)('pagination.rowsPerPage'),
                                            "items-count-separator": _unref(t)('pagination.of'),
                                            "previous-label": _unref(t)('pagination.previous'),
                                            "next-label": _unref(t)('pagination.next'),
                                            "go-to-page-label": _unref(t)('pagination.goToPage'),
                                            "go-label": _unref(t)('pagination.go'),
                                            "show-go-to-page": "",
                                            "data-testid": "project-allocations-pager",
                                            onPageChange: _unref(tableQuery).setPage,
                                            onItemsPerPageChange: _unref(tableQuery).setPageSize
                                        }, null, 8 /* PROPS */, ["current-page", "total-pages", "total-items", "items-per-page", "rows-per-page-label", "items-count-separator", "previous-label", "next-label", "go-to-page-label", "go-label", "onPageChange", "onItemsPerPageChange"])
                                    ]),
                                    _: 1 /* STABLE */
                                }, 8 /* PROPS */, ["query", "views", "labels", "column-settings", "search-placeholder", "loading"]),
                                _createVNode(ListFloatActionBar, {
                                    "selection-count": _unref(effectiveCount),
                                    "all-pages-selected": _unref(allPagesSelected),
                                    "total-across-all-pages": _unref(totalAcrossAllPages),
                                    actions: bulkActions.value,
                                    "is-loading": exporting.value,
                                    onDismiss: _unref(clearSelection),
                                    onSelectAllPages: _unref(onSelectAllPages),
                                    onActionClick: onBulkAction
                                }, null, 8 /* PROPS */, ["selection-count", "all-pages-selected", "total-across-all-pages", "actions", "is-loading", "onDismiss", "onSelectAllPages"])
                            ], 64 /* STABLE_FRAGMENT */))
                    ]),
                    _: 1 /* STABLE */
                }, 8 /* PROPS */, ["header", "loading"]),
                _createVNode(_unref(Modal), {
                    modelValue: detailOpen.value,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((detailOpen).value = $event)),
                    size: "sm",
                    "aria-labelledby": _unref(detailTitleId)
                }, {
                    header: _withCtx(() => [
                        _createElementVNode("h3", {
                            id: _unref(detailTitleId),
                            class: "text-lg font-bold text-(--colors-text-heading)"
                        }, _toDisplayString(_unref(t)("projects.detail.allocations.detail.title")), 9 /* TEXT, PROPS */, _hoisted_7)
                    ]),
                    footer: _withCtx(() => [
                        _createVNode(_unref(Button), {
                            variant: "secondary",
                            label: _unref(t)('common.close'),
                            "data-testid": "project-allocations-detail-close",
                            onClick: _cache[2] || (_cache[2] = ($event) => (detailOpen.value = false))
                        }, null, 8 /* PROPS */, ["label"]),
                        _createVNode(_unref(Button), {
                            variant: "primary",
                            label: _unref(t)('projects.detail.allocations.detail.open'),
                            "data-testid": "project-allocations-detail-open",
                            onClick: openDonationRecord
                        }, null, 8 /* PROPS */, ["label"])
                    ]),
                    default: _withCtx(() => [
                        (detailRow.value)
                            ? (_openBlock(), _createElementBlock("dl", _hoisted_8, [
                                _createElementVNode("div", _hoisted_9, [
                                    _createElementVNode("dt", _hoisted_10, _toDisplayString(_unref(t)("projects.detail.allocations.detail.donor")), 1 /* TEXT */),
                                    _createElementVNode("dd", _hoisted_11, _toDisplayString(detailRow.value.donor), 1 /* TEXT */)
                                ]),
                                _createElementVNode("div", _hoisted_12, [
                                    _createElementVNode("dt", _hoisted_13, _toDisplayString(_unref(t)("projects.detail.allocations.detail.amount")), 1 /* TEXT */),
                                    _createElementVNode("dd", _hoisted_14, _toDisplayString(detailRow.value.amount), 1 /* TEXT */)
                                ]),
                                _createElementVNode("div", _hoisted_15, [
                                    _createElementVNode("dt", _hoisted_16, _toDisplayString(_unref(t)("projects.detail.allocations.detail.date")), 1 /* TEXT */),
                                    _createElementVNode("dd", _hoisted_17, _toDisplayString(detailRow.value.date), 1 /* TEXT */)
                                ]),
                                _createElementVNode("div", _hoisted_18, [
                                    _createElementVNode("dt", _hoisted_19, _toDisplayString(_unref(t)("projects.detail.allocations.detail.project")), 1 /* TEXT */),
                                    _createElementVNode("dd", _hoisted_20, _toDisplayString(__props.projectName), 1 /* TEXT */)
                                ])
                            ]))
                            : _createCommentVNode("v-if", true)
                    ]),
                    _: 1 /* STABLE */
                }, 8 /* PROPS */, ["modelValue", "aria-labelledby"])
            ]));
        };
    }
});
